'use client'

import Link from 'next/link'
import { useMemo, useSyncExternalStore } from 'react'
import { track } from '@/utils/analytics'
import { closestPages, type PageCandidate, type ScoredCandidate } from '@/utils/closestPages'

// A single suggestion is headlined only when the match is strong; weaker
// matches are still offered, as a list, because a person can judge them.
const HEADLINE_SCORE = 0.5

// The path never changes while this page is showing, so there is nothing to
// subscribe to. The server snapshot is null: suggestions render after
// hydration, and the server HTML stays a plain 404.
const noSubscribe = () => () => {}
const getPath = () => window.location.pathname
const getServerPath = () => null

/**
 * "Did you mean" on the 404 page. Reads the path client-side (not-found.tsx
 * has no route params) and ranks it against every real page. See
 * utils/closestPages.ts for why and how.
 */
export default function DidYouMean({ candidates }: { candidates: PageCandidate[] }) {
  const path = useSyncExternalStore(noSubscribe, getPath, getServerPath)
  const matches: ScoredCandidate[] = useMemo(
    () => (path ? closestPages(path, candidates) : []),
    [path, candidates]
  )

  if (matches.length === 0) return null
  const [best, ...rest] = matches
  const headline = best.score >= HEADLINE_SCORE
  const listed = headline ? rest : matches

  const onClick = (href: string, rank: number) =>
    track('related_article_click', {
      cta_surface: 'not_found_did_you_mean',
      page_type: 'not_found',
      destination: href,
      reason: `rank_${rank}`,
    })

  return (
    <div className="max-w-xl mx-auto mb-10 rounded-2xl border border-forest/25 bg-white p-5 shadow-sm">
      {headline ? (
        <p className="text-lg text-gray-800">
          Did you mean{' '}
          <Link href={best.href} onClick={() => onClick(best.href, 1)} className="font-semibold text-forest underline">
            {best.title}
          </Link>
          ?
        </p>
      ) : null}
      {listed.length > 0 ? (
        <>
          <p className={`text-sm font-semibold text-gray-600 ${headline ? 'mt-4' : ''}`}>
            {headline ? 'Other close matches' : 'Pages with a similar address'}
          </p>
          <ul className="mt-2 space-y-1.5">
            {listed.map((m, i) => (
              <li key={m.href}>
                <Link
                  href={m.href}
                  onClick={() => onClick(m.href, headline ? i + 2 : i + 1)}
                  className="text-forest underline"
                >
                  {m.title}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}
